/**
 * Textos de estado PJUD para el cliente: Monitoreo PJUD (grilla) y modal PJUD del requerimiento.
 *
 * Regla: el cliente nunca ve la jerga del robot (`scrapingdescription`: emojis, "procesando,
 * consultando movimientos", reintentos). Se traduce a algo accionable según el `errorType`.
 * Origen: 2ª revisión de Natalia del Monitoreo PJUD (14-oct-2026).
 */

/** Antes "Sin acceso al detalle": en OXXO convivía con "sin acceso de detalle, causa reservada" y no se entendía. */
export const RESERVED_LABEL = 'Causa reservada';

/** Fecha de calendario sin hora ('2026-10-16'): no tiene zona horaria y no se convierte. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Partes día/mes/año en hora de Chile. */
function chileParts(d: Date): { day: string; month: string; year: string } {
  const parts = new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric',
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value || '';
  return { day: get('day'), month: get('month'), year: get('year') };
}

/**
 * dd/MM/yyyy en hora de Chile, o null si la fecha no viene o no es válida. Una fecha SIN hora se
 * muestra tal cual: new Date('2026-10-16') es medianoche UTC, que en Chile ya es el 15/10.
 */
export function formatChileDate(value?: string | Date | null): string | null {
  if (!value) return null;
  const m = typeof value === 'string' ? DATE_ONLY.exec(value) : null;
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const { day, month, year } = chileParts(d);
  return `${day}/${month}/${year}`;
}

/** Clave AAAAMMDD del día (en Chile) de una fecha; las fechas sin hora se toman tal cual. */
function chileDayKey(value: string | Date): string | null {
  const m = typeof value === 'string' ? DATE_ONLY.exec(value) : null;
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const { day, month, year } = chileParts(d);
  return `${year}${month}${day}`;
}

/**
 * Tooltip de una causa conectada que espera su próxima pasada del robot.
 * Si la fecha ya pasó, no se promete una revisión en el pasado: se dice que está pendiente desde
 * entonces (validado en prod, femsa 14-oct: había causas con nextReview del 26/09).
 */
export function pendingReviewTooltip(nextReview?: string | Date | null, now: Date = new Date()): string {
  const date = formatChileDate(nextReview);
  if (!date) return 'En proceso de revisión: la causa está conectada y en la cola de revisión del robot.';
  // Con hora se compara el instante; sin hora (fecha de calendario), el día: hoy no está vencida.
  const isDateOnly = typeof nextReview === 'string' && DATE_ONLY.test(nextReview);
  const notOverdue = isDateOnly
    ? (chileDayKey(nextReview as string) || '') >= (chileDayKey(now) || '')
    : new Date(nextReview as string | Date).getTime() >= now.getTime();
  return notOverdue
    ? `En proceso de revisión, programada para el ${date}. La causa está conectada; el robot la volverá a revisar en esa fecha.`
    : `En proceso de revisión: estaba programada para el ${date} y sigue pendiente. La causa está conectada y en la cola del robot.`;
}

export interface PjudCauseStatusInput {
  rit?: string | null;
  tribunal?: string | null;
  errorType?: string | null;
  /** La BD de causas devuelve columnas en minúscula; se aceptan ambas formas. */
  errortype?: string | null;
  scrapingstatus?: string | null;
  scrapingdescription?: string | null;
  retries?: number | null;
  /** Nombre real del campo en la API del robot (/robotpjud/get). */
  scrapingRetries?: number | null;
  scrapingdate?: string | Date | null;
}

/** Mensaje para el cliente cuando la causa no trae movimientos (modal PJUD del requerimiento). */
export function causeStatusMessage(cause: PjudCauseStatusInput): string {
  const err = cause.errorType || cause.errortype || null;
  const ref = [cause.rit, cause.tribunal].filter(Boolean).join(' – ') || 'La causa';
  const subject = ref === 'La causa' ? ref : `La causa ${ref}`;

  const rawRetries = cause.scrapingRetries ?? cause.retries;
  const n = typeof rawRetries === 'number' && rawRetries > 0 ? rawRetries : null;
  const date = formatChileDate(cause.scrapingdate);
  const attempts = n ? ` en ${n} ${n === 1 ? 'intento' : 'intentos'}` : '';
  const when = date ? ` (último el ${date})` : '';

  switch (err) {
    case 'not_found':
    case 'not_found_confirmed':
      return `${subject} fue consultada en el PJUD${attempts}${when} y no fue posible encontrarla. ` +
        'Verifica que los datos sean correctos e intenta nuevamente.';
    case 'reserved_cause':
      return `${subject} aparece como reservada (privada) en el PJUD, por eso no se puede leer su detalle. ` +
        'Para verla se necesita la Clave Única de alguien que sea parte en la causa.';
    case 'configuration_error':
      return `${subject} no coincide con el PJUD: revisa que el tribunal o la competencia sean correctos e intenta nuevamente.`;
    case 'auth_failed':
    case 'credential_no_access':
      return `${subject} es privada y la clave del cliente para el PJUD requiere renovarse para poder leerla.`;
    case 'session_expired':
      return `${subject} está en revisión: la sesión con el PJUD expiró y se reintentará automáticamente.`;
  }

  if (cause.scrapingstatus === 'En progreso' || cause.scrapingstatus === 'Pendiente') {
    return `${subject} está en la cola de revisión del robot. En unos minutos la información aparecerá aquí.`;
  }
  return 'No se encontraron movimientos registrados.';
}

export type ConnectedSort = 'sync' | 'movement';

/** Flecha de orden de "Último movimiento" en Conectadas: alterna siempre entre los dos órdenes. */
export function nextConnectedSort(current: ConnectedSort): ConnectedSort {
  return current === 'movement' ? 'sync' : 'movement';
}

/**
 * Motivo de una causa en "Requieren atención". Si hay errorType se muestra siempre, aunque el robot
 * esté reintentando: antes se ocultaba con la descripción transitoria y dos causas reservadas
 * mostraban información distinta. Sin errorType no hay motivo accionable (solo jerga del robot).
 */
export function attentionReasonDetail(errorType?: string | null): string | null {
  switch (errorType) {
    case 'not_found':
    case 'not_found_confirmed':
      return 'La causa no se encontró en el PJUD con el RIT/tribunal indicados. Revisa que los datos sean correctos y vuelve a intentar.';
    case 'configuration_error':
      return 'El tribunal o la competencia no coinciden con el PJUD. Corrige esos datos y reintenta.';
    case 'reserved_cause':
      return 'La causa está reservada en el PJUD; se requiere Clave Única del cliente (ser parte en la causa) para leer el detalle.';
    case 'auth_failed':
    case 'credential_no_access':
      return 'La clave del cliente para el PJUD requiere renovarse para poder leer esta causa.';
    case 'session_expired':
      return 'La sesión con el PJUD expiró; se reintentará automáticamente.';
    default:
      return null;
  }
}

/** "Estado procesal · Etapa" de una causa conectada (p. ej. "Tramitación · Audiencia Preparatoria"). */
export function proceduralStateLabel(causeStatus?: string | null, stage?: string | null): string {
  const parts = [causeStatus, stage].map(v => (v || '').trim()).filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}
